import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { announcementsApi } from '../../../api/adminApi';
import {
  announcementId,
  announcementBody,
  announcementDateInputValue,
  formatAnnouncementDateShort,
  todayLocalISODate,
  ANNOUNCEMENT_TITLE_MAX_LENGTH,
} from './announcementHelpers';
import './AnnouncementView.css';

function AnnouncementView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formAnnouncementDate, setFormAnnouncementDate] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadOne = (opts = {}) => {
    const silent = Boolean(opts.silent);
    if (!id) return Promise.resolve();
    if (!silent) setLoading(true);
    setError('');
    return announcementsApi
      .getOne(id)
      .then((data) => setItem(data))
      .catch((err) => setError(err.userMessage || 'Failed to load announcement'))
      .finally(() => {
        if (!silent) setLoading(false);
      });
  };

  useEffect(() => {
    loadOne();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openEdit = () => {
    if (!item) return;
    setFormTitle((item.title ?? '').slice(0, ANNOUNCEMENT_TITLE_MAX_LENGTH));
    setFormDescription(announcementBody(item));
    setFormAnnouncementDate(announcementDateInputValue(item) || todayLocalISODate());
    setFormError('');
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setFormError('');
  };

  const handleSave = (e) => {
    e.preventDefault();
    setFormError('');
    const aid = announcementId(item);
    if (!aid) return;
    if (!formTitle.trim()) {
      setFormError('Title is required.');
      return;
    }
    if (formTitle.trim().length > ANNOUNCEMENT_TITLE_MAX_LENGTH) {
      setFormError(`Title must be at most ${ANNOUNCEMENT_TITLE_MAX_LENGTH} characters.`);
      return;
    }
    if (!formAnnouncementDate || !/^\d{4}-\d{2}-\d{2}$/.test(formAnnouncementDate)) {
      setFormError('Announcement date is required (use a valid date).');
      return;
    }
    const nextTitle = formTitle.trim();
    const nextDesc = formDescription;
    const nextDate = formAnnouncementDate;
    const prevTitle = (item.title ?? '').trim();
    const prevDesc = announcementBody(item);
    const prevDate = announcementDateInputValue(item);
    if (nextTitle === prevTitle && nextDesc === prevDesc && nextDate === prevDate) {
      setFormError('Change the title, description, or date before saving.');
      return;
    }

    setFormSaving(true);
    announcementsApi
      .update(aid, {
        title: nextTitle,
        description: nextDesc,
        announcement_date: nextDate,
      })
      .then(() => loadOne({ silent: true }))
      .then(() => closeEdit())
      .catch((err) => setFormError(err.userMessage || 'Failed to save.'))
      .finally(() => setFormSaving(false));
  };

  const handleDelete = () => {
    const aid = announcementId(item);
    if (!aid) return;
    setDeleting(true);
    announcementsApi
      .delete(aid)
      .then(() => navigate('/admin/announcements', { replace: true }))
      .catch((err) => {
        setError(err.userMessage || 'Failed to delete');
        setDeleteOpen(false);
      })
      .finally(() => setDeleting(false));
  };

  const formatDate = (str) => {
    if (!str) return '—';
    try {
      const d = new Date(str);
      return Number.isNaN(d.getTime())
        ? str
        : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return str;
    }
  };

  if (loading) {
    return <div className="ann-view-loading">Loading announcement…</div>;
  }

  if (error && !item) {
    return (
      <div className="ann-view-wrap">
        <div className="ann-view-header">
          <Link to="/admin/announcements" className="ann-view-back">
            ← Announcements
          </Link>
        </div>
        <div className="ann-view-error" role="alert">
          {error}
        </div>
      </div>
    );
  }

  const aid = announcementId(item);
  const body = announcementBody(item);

  return (
    <div className="ann-view-wrap">
      <div className="ann-view-header">
        <Link to="/admin/announcements" className="ann-view-back">
          ← Announcements
        </Link>
        <h2 className="ann-view-title">{item?.title || 'Announcement'}</h2>
        <div className="ann-view-actions">
          <button type="button" className="ann-view-btn ann-view-btn-edit" onClick={openEdit} disabled={!aid}>
            Edit announcement
          </button>
          <button type="button" className="ann-view-btn ann-view-btn-delete-outline" onClick={() => setDeleteOpen(true)}>
            Delete
          </button>
        </div>
      </div>

      {error && (
        <div className="ann-view-error ann-view-error--inline" role="alert">
          {error}
        </div>
      )}

      <div className="ann-view-card">
        <dl className="ann-view-meta">
          <div className="ann-view-row">
            <dt>Announcement date</dt>
            <dd>{formatAnnouncementDateShort(announcementDateInputValue(item))}</dd>
          </div>
          <div className="ann-view-row">
            <dt>Created</dt>
            <dd>{formatDate(item?.created_at)}</dd>
          </div>
          {item?.updated_at && item.updated_at !== item.created_at && (
            <div className="ann-view-row">
              <dt>Updated</dt>
              <dd>{formatDate(item.updated_at)}</dd>
            </div>
          )}
          {item?.creator?.email && (
            <div className="ann-view-row">
              <dt>Author</dt>
              <dd>
                {item.creator.name ? `${item.creator.name} · ${item.creator.email}` : item.creator.email}
              </dd>
            </div>
          )}
        </dl>
        <div className="ann-view-body">
          <h3 className="ann-view-body-heading">Description</h3>
          {body ? (
            <div className="ann-view-body-text">{body}</div>
          ) : (
            <p className="ann-view-body-empty">No description.</p>
          )}
        </div>
      </div>

      <Modal
        isOpen={editOpen}
        onRequestClose={() => {
          if (!formSaving) closeEdit();
        }}
        contentLabel="Edit announcement"
        className="ann-view-modal-content"
        overlayClassName="ann-view-modal-overlay"
      >
        <div className="ann-view-modal-inner">
          <h3 id="ann-view-edit-title">Edit announcement</h3>
          <form onSubmit={handleSave}>
            {formError && (
              <div className="ann-view-form-error" role="alert">
                {formError}
              </div>
            )}
            <div className="ann-view-field">
              <div className="ann-view-label-row">
                <label htmlFor="ann-view-title">Title *</label>
                <span className="ann-view-char-count" aria-live="polite">
                  {formTitle.length}/{ANNOUNCEMENT_TITLE_MAX_LENGTH}
                </span>
              </div>
              <input
                id="ann-view-title"
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                disabled={formSaving}
                maxLength={ANNOUNCEMENT_TITLE_MAX_LENGTH}
                aria-describedby="ann-view-title-hint"
              />
              <p id="ann-view-title-hint" className="ann-view-field-hint">
                Maximum {ANNOUNCEMENT_TITLE_MAX_LENGTH} characters.
              </p>
            </div>
            <div className="ann-view-field">
              <label htmlFor="ann-view-description">Description</label>
              <textarea
                id="ann-view-description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                rows={6}
                disabled={formSaving}
              />
            </div>
            <div className="ann-view-field">
              <label htmlFor="ann-view-date">Announcement date *</label>
              <input
                id="ann-view-date"
                type="date"
                value={formAnnouncementDate}
                onChange={(e) => setFormAnnouncementDate(e.target.value)}
                disabled={formSaving}
              />
            </div>
            <div className="ann-view-modal-actions">
              <button type="button" className="ann-view-modal-cancel" onClick={closeEdit} disabled={formSaving}>
                Cancel
              </button>
              <button type="submit" className="ann-view-btn ann-view-btn-submit" disabled={formSaving}>
                {formSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {deleteOpen && (
        <div className="ann-view-delete-modal" role="dialog" aria-modal="true" aria-labelledby="ann-view-del-title">
          <div className="ann-view-delete-backdrop" onClick={() => !deleting && setDeleteOpen(false)} aria-hidden />
          <div className="ann-view-delete-box">
            <h3 id="ann-view-del-title">Delete this announcement?</h3>
            <p>This soft-deletes the announcement. This cannot be undone from the admin UI.</p>
            <div className="ann-view-delete-actions">
              <button
                type="button"
                className="ann-view-btn ann-view-btn-muted"
                onClick={() => setDeleteOpen(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button type="button" className="ann-view-btn ann-view-btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnnouncementView;
